import { useState, useEffect } from "react";
import "../../styles/TradingDashboard.css";
import axios from "axios";
import Loader from "../../components/utilComponents/loader.js";

const symbols = [
  "GBPUSD",
  "USDJPY",
  "EURUSD",
  "XAUUSD.r",
  "USDCHF",
  "AUDUSD",
  "NZDUSD",
  "USDCAD",
  "USOIL",
];

export default function TradingDashboard() {
  const [selectedSymbol, setSelectedSymbol] = useState("EURUSD");
  const [selectedDate, setSelectedDate] = useState(
    () => new Date().toISOString().split("T")[0]
  ); // Default: today
  const [predictions, setPredictions] = useState({});
  const [news, setNews] = useState([]);
  const [recommendation, setRecommendation] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state

  useEffect(() => {
    fetchPredictions();
    fetchNews();
    fetchGenAIRecommendation();
  }, [selectedSymbol, selectedDate]);

  const fetchPredictions = () => {
    const directions = {
      "1H": "Bullish",
      "4H": "Neutral",
      "1D": "Bearish",
    };

    // Simulate base prices (e.g., mock data or coming from your backend later)
    const basePrice = 1.08 + Math.random() * 0.01; // Simulate near 1.08

    const getPredictionData = (direction, multiplier = 1) => {
      const predictedValue = parseFloat(
        (basePrice + Math.random() * 0.002 * multiplier).toFixed(5)
      );
      let stopLoss;

      if (direction === "Bullish") {
        stopLoss = parseFloat((predictedValue - 0.001).toFixed(5));
      } else if (direction === "Bearish") {
        stopLoss = parseFloat((predictedValue + 0.001).toFixed(5));
      } else {
        stopLoss = parseFloat((predictedValue - 0.001).toFixed(5)); // neutral treated like bullish
      }

      return {
        direction,
        predictedValue,
        stopLoss,
      };
    };

    const newPredictions = {
      "1H": getPredictionData(directions["1H"]),
      "4H": getPredictionData(directions["4H"]),
      "1D": getPredictionData(directions["1D"]),
    };

    setPredictions(newPredictions);
  };

  const fetchNews = async () => {
    const postData = {
      symbol: selectedSymbol,
      date: selectedDate,
    };
    setLoading(true); // Start loading
    try {
      const response = await axios.post("http://127.0.0.1:5000/News", postData);
      const articles = response.data;

      const mappedNews = articles.map((article) => ({
        title: article.title || "No Title",
        body:
          article.body?.split("\n").filter((line) => line.trim() !== "") || [],
        sentiment: formatSentiment(article.sentiment),
        dateTime: article.dateTime || new Date().toISOString(),
      }));

      setNews(mappedNews);
    } catch (error) {
      console.error("Error fetching news from backend:", error);
      setNews([{ title: "Failed to load news", body: [], sentiment: "error" }]);
    } finally {
      setLoading(false); // End loading
    }
  };
  const formatSentiment = (sentimentValue) => {
    if (typeof sentimentValue === "string") {
      sentimentValue = parseFloat(sentimentValue);
    }

    if (sentimentValue > 0.05) {
      return "Positive";
    } else if (sentimentValue < -0.05) {
      return "Negative";
    } else {
      return "Neutral";
    }
  };

  const fetchGenAIRecommendation = async () => {
    const formattedDate = selectedDate.replace(/-/g, "");

    const postData = {
      symbol: selectedSymbol,
      date: formattedDate,
    };

    setLoading(true);
    try {
      const fetch_newsresponse = await axios.post(
        "http://127.0.0.1:4000/fetch-news",
        postData
      );
      console.log("Response from backend:", fetch_newsresponse.data); // Log the response

      // Ensure the response is always treated as an array
      const articles = Array.isArray(fetch_newsresponse.data)
        ? fetch_newsresponse.data
        : [fetch_newsresponse.data];

      // Map the articles to the structure you need
      const mappedNews = articles.map((article) => ({
        title: article.title || "No Title",
        body_preview:
          article.body_preview
            ?.split("\n")
            .filter((line) => line.trim() !== "")
            .slice(0, 3) || [],
        sentiment: article.sentiment,
        generated_analysis: article.generated_analysis,
      }));

      setRecommendation(mappedNews); // Store mapped news
    } catch (error) {
      console.error("Error fetching news from backend:", error);
      setNews([
        { title: "Failed to load news", body_preview: [], sentiment: "error" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Loader />} {/* Show loader when loading */}
      {/* Symbol and Date Selector */}
      <div className="dropdown-wrapper">
        <div className="symbol-selector-dropdown">
          <select
            className="symbol-select"
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}>
            {symbols.map((symbol) => (
              <option key={symbol} value={symbol}>
                {symbol}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="date-picker"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>
      <div className="container1">
        {/* Left Column - Predictions */}
        <div className="left-panel">
          {["1H", "4H", "1D"].map((timeframe) => (
            <div key={timeframe} className="card prediction-card">
              <h2 className="trading-card-title">{timeframe} Prediction</h2>
              {typeof predictions[timeframe] === "object" ? (
                <>
                  <p className="card-value">
                    Predicted: {predictions[timeframe].predictedValue}
                  </p>
                  <p className="card-value">
                    S/L: {predictions[timeframe].stopLoss}
                  </p>
                  <p className="card-value">
                    Direction: {predictions[timeframe].direction}
                  </p>
                </>
              ) : (
                <p className="card-value">{predictions[timeframe]}</p>
              )}
            </div>
          ))}
        </div>

        {/* Right Column - News  */}
        <div className="right-panel">
          <div className="card half-height-card">
            <h2 className="trading-card-title">Symbol News</h2>
            <ul className="news-list">
              {news.map((item, idx) => (
                <li key={idx} className="news-item">
                  <p className="news-title">{item.title}</p>
                  <div className="news-body">
                    {Array.isArray(item.body) ? (
                      item.body.map((line, lineIdx) => (
                        <p key={lineIdx} className="news-line">
                          {line}
                        </p>
                      ))
                    ) : (
                      <p>{item.body}</p>
                    )}
                  </div>
                  <p className="news-sentiment">Sentiment: {item.sentiment}</p>
                  <p className="news-date-time">
                    Date: {new Date(item.dateTime).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          {/* Analysis & Recommendation */}

          <div className="card half-height-card">
            <h2 className="trading-card-title"> Analysis & Recommendation</h2>
            <ul className="news-list">
              {recommendation.map((item, idx) => (
                <li key={idx} className="news-item">
                  <p className="news-title">{item.title}</p>
                  <div className="news-body">
                    {Array.isArray(item.body_preview) ? (
                      item.body_preview.map((line, lineIdx) => (
                        <p key={lineIdx} className="news-line">
                          {line}
                        </p>
                      ))
                    ) : (
                      <p>{item.body_preview}</p>
                    )}
                  </div>
                  <p className="news-sentiment">Sentiment: {item.sentiment}</p>
                  <div className="analysis-section">
                    <h3>Analysis:</h3>
                    <p className="analysis-content">
                      {item.generated_analysis}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
